import {  Dispatch } from 'redux';
import apis from '../../../utils/apis';
import {
  FETCH_EMPLOYEES_REQUEST, FETCH_EMPLOYEES_SUCCESS, FETCH_EMPLOYEES_FAILURE, ONBOARD_EMPLOYEE_REQUEST, ONBOARD_EMPLOYEE_SUCCESS,
  FETCH_EMPLOYEE_DETAILS_FAILURE, FETCH_EMPLOYEE_DETAILS_REQUEST, FETCH_EMPLOYEE_DETAILS_SUCCESS, AppThunk
} from '../../types';
import { showFeedbackModal } from '../UserFeedbackActions';
import { handleApiError } from '../utils/utils';
import { Employee } from '../../../components/Management/Employees/types';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import { readErrorMessage, toErrorText } from '../../../utils/errorMessage';

export const fetchAllEmployees = (params: {
  searchTerm?: string;
  filters?: { [key: string]: string[] | boolean };
  sortBy?: string;
  sortOrder?: string;
  page?: number;
  limit?: number;
} = {}): AppThunk<Promise<{ employees: Employee[], total: number, currentPage: number, totalPages: number } | null>> => async (dispatch) => {
  dispatch({ type: FETCH_EMPLOYEES_REQUEST });
  try {
    const {
      searchTerm = '',
      filters = {},
      sortBy = '',
      sortOrder = 'asc',
      page = 1,
      limit = 10,
    } = params;

    const filtersJson = JSON.stringify(filters);

    const response = await apis({
      method: 'GET',
      url: '/org-people/people',
      params: {
        searchTerm,
        filters: filtersJson,
        sortBy,
        sortOrder,
        page,
        limit,
      },
    });

    if (response && response.status === 200) {
      
      const employees = (response.data.people || []).map((person: any) => ({
        ...person,
        departments: person.departments?.map((d: any) => ({ ...d, name: decodeHtmlEntities(d.name) })),
        teams: person.teams?.map((t: any) => ({ ...t, name: decodeHtmlEntities(t.name) })),
      }));
      const total = response.data.count || 0;
      const currentPage = response.data.page || 1;
      const totalPages = response.data.totalPages || 1;

      dispatch({
        type: FETCH_EMPLOYEES_SUCCESS,
        payload: {
          employees,
          total,
          currentPage,
          totalPages,
        },
      });
      return { employees, total, currentPage, totalPages };
    } else {
      dispatch({
        type: FETCH_EMPLOYEES_FAILURE,
        payload: 'Failed to fetch employees.',
      });
      handleApiError(response, dispatch);
      return null;
    }
  } catch (error: any) {
    handleApiError(error, dispatch);
    dispatch({
      type: FETCH_EMPLOYEES_FAILURE,
      payload: error.message || 'An error occurred while fetching employees.',
    });
    return null;
  }
};


export const sendEmployeeInvite = (inviteData: {
  email?: string;
  emails?: string[];
  role?: string;
  invites?: Array<{ email: string; role: string }>;
}) => {
  return async (dispatch: Dispatch<any>) => {
    dispatch({ type: ONBOARD_EMPLOYEE_REQUEST });
    try {
      let payload: any;

      // Handle single email with role
      if (inviteData.invites && inviteData.invites.length > 0) {
        payload = { invites: inviteData.invites };
      } else if (inviteData.emails && inviteData.emails.length > 1) {
        payload = { emails: inviteData.emails };
      } else {
        const email = inviteData.email || (inviteData.emails?.[0] || '');
        payload = { email };
        if (inviteData.role) {
          payload.role = inviteData.role;
        }
      }

      const response = await apis.post('/org-people/invite', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response && response.status === 200) {
        dispatch({
          type: ONBOARD_EMPLOYEE_SUCCESS
        });
        const count = inviteData.invites ? inviteData.invites.length : inviteData.emails ? inviteData.emails.length : 1;
        dispatch(
          showFeedbackModal({
            modalType: 'success',
            message: count > 1 ? `${count} invitations sent successfully.` : 'Invitation sent successfully.',
            showModal: true,
            duration: 3000,
          })
        );
        return response;
      } else {
        handleApiError(response, dispatch);
        throw new Error('Failed to send invitations');
      }
    } catch (error: any) {
      handleApiError(error, dispatch);
      throw error;
    }
  };
};

export const fetchEmployeeDetails = (employeeId: string) => async (dispatch: any) => {
  dispatch({ type: FETCH_EMPLOYEE_DETAILS_REQUEST });
  try {

    const response = await apis.get(`/user-profile/user/uuid/${employeeId}`);

    if (response && response.status === 200) {
      dispatch({
        type: FETCH_EMPLOYEE_DETAILS_SUCCESS,
        payload: response.data,
      });
      return response.data;
    } else {
      const errorMsg = toErrorText(response?.data?.message) || toErrorText(response?.data?.error) || 'Failed to fetch employee details';
      dispatch({
        type: FETCH_EMPLOYEE_DETAILS_FAILURE,
        payload: errorMsg,
      });
      handleApiError(
        {
          response: {
            status: response?.status,
            data: { message: errorMsg }
          }
        },
        dispatch
      );
      return null;
    }
  } catch (error: any) {
    const statusCode = error?.response?.status;
    const errorMsg = readErrorMessage(error, 'Failed to fetch employee details');

    console.error('[ERROR] Error fetching employee details');
    console.error('  Status Code:', statusCode);
    console.error('  Error Message:', errorMsg);

    if (statusCode === 403) {
      console.error('  → Access Denied: You may not have permission to view this employee');
    } else if (statusCode === 404) {
      console.error('  → Not Found: Employee may have been deleted or ID may be incorrect');
    } else if (statusCode === 401) {
      console.error('  → Unauthorized: Your session may have expired, please log in again');
    }

    dispatch({
      type: FETCH_EMPLOYEE_DETAILS_FAILURE,
      payload: errorMsg,
    });
    handleApiError(error, dispatch);
    return null;
  }
};
